import { useGetOrganizationById, UseGetOrganizationByIdReturnData } from "@/features/organization/hooks/useQuery/useGetOrganizationById";
import { useGetProjectById, UseGetProjectByIdReturn } from "@/lib/useQuery/project/useGetProjectById";
import { useOrganizationStore, useProjectStore, useUserStore } from "@/lib/zustand/useStore";
import { UseCheckProjectAccessProps, UseCheckProjectAccessReturn } from "./useCheckProjectAccess.types";

export function useCheckProjectAccess({
    projectId,
    orgId,
    userId,
    requiredPermissions,
    any = false,
    roles
}: UseCheckProjectAccessProps): UseCheckProjectAccessReturn {
    const storeUser = useUserStore();
    const { data: fetchedProject } = useGetProjectById({ projectId });
    const { project: storeProject } = useProjectStore();
    const { organization: storeOrganization } = useOrganizationStore();
    const { data: fetchedOrganization } = useGetOrganizationById(orgId);
    
    let organization: UseGetOrganizationByIdReturnData | null | undefined = storeOrganization;
    
    if (orgId) {
        organization = fetchedOrganization;
    }
    
    if (!organization) {
        return {
            hasAccess: false,
            mergedPermissions: {},
        };
    }
    
    if (!userId) {
        userId = storeUser.id ?? undefined;
    }

    let project: UseGetProjectByIdReturn | null | undefined = storeProject;
    if (projectId) {
        project = fetchedProject;
    }
    
    if (!project) {
        return {
            hasAccess: false,
            mergedPermissions: {},
        };
    }

    const userMember = project.project_members?.find(m => m.user_id === userId);
    const projectPermissions = userMember?.role || {};
    const projectRole = userMember?.role;

    const orgMember = organization?.organisation_members?.find(m => m.user_id === userId);
    const orgPermissions = orgMember?.role || {};
    const orgRole = orgMember?.role;

    // Check if user is a member of either the project or organization
    if (!userMember && !orgMember) {
        return {
            hasAccess: false,
            mergedPermissions: {},
        };
    }

    if (roles) {
        const requiredRoles = Array.isArray(roles) ? roles : [roles];
        const userProjectRoleType = projectRole?.role;
        const userOrgRoleType = orgRole?.role;

        const hasRequiredRole = (
            userProjectRoleType 
            ? requiredRoles.includes(userProjectRoleType) 
            : false
        ) || (
            userOrgRoleType 
            ? requiredRoles.includes(userOrgRoleType) 
            : false
        );
        if (!hasRequiredRole) {
            return {
                hasAccess: false,
                mergedPermissions: { ...projectPermissions, ...orgPermissions },
            };
        }
    }

    const mergedPermissions: Record<string, boolean> = { ...projectPermissions, ...orgPermissions };

    Object.keys(orgPermissions).forEach(key => {
        const orgValue = orgPermissions[key as keyof typeof orgPermissions];
        const projectValue = projectPermissions[key as keyof typeof projectPermissions];
        if (orgValue === true) {
            mergedPermissions[key] = true;
        } else if (projectValue !== undefined) {
            mergedPermissions[key] = projectValue as boolean;
        }
    });

    // Check permissions if any are specified
    const requiredPermissionEntries = Object.entries(requiredPermissions).filter(([, value]) => value === true);
    
    // If no permissions are required and user is a member, return true
    if (requiredPermissionEntries.length === 0) {
        return {
            hasAccess: true,
            mergedPermissions,
        };
    }

    // Check permissions based on 'any' flag
    const hasAccess = any 
        ? requiredPermissionEntries.some(([key]) => {
            return mergedPermissions[key] === true;
          })
        : requiredPermissionEntries.every(([key]) => {
            return mergedPermissions[key] === true;
          });

    return {
        hasAccess,
        mergedPermissions,
        roles: {
            orgRole: orgRole?.role,
            projectRole: projectRole?.role,
        }
    };
}